import { useState } from "react";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} TopSchool
 * @property {number} school_id - ID of the top performing school
 * @property {string} school_name - Name of the top performing school
 */

/**
 * @typedef {object} SubSchoolStats
 * @property {number} total_schools - Total number of sub-schools
 * @property {number} total_students - Total number of students across all sub-schools
 * @property {number} avg_completion_rate - Average completion rate across all sub-schools
 * @property {number} avg_quiz_accuracy - Average quiz accuracy across all sub-schools
 * @property {TopSchool} top_school - Top performing school details
 */

/**
 * @typedef {object} SubSchoolReportItem
 * @property {number} school_id - ID of the school
 * @property {string} school_name - Name of the school
 * @property {number} students - Total number of students in the school
 * @property {number} avg_completion - Average completion percentage
 * @property {number} avg_quiz_score - Average quiz score
 * @property {number} pass_rate - Pass rate percentage
 * @property {number} avg_attempts - Average number of quiz attempts
 * @property {number} licenses_assigned - Number of licenses assigned to the school
 * @property {number} licenses_used - Number of licenses used by the school
 * @property {number} teachers - Number of teachers in the school
 * @property {string} school_admin_name - Name of the school admin
 * @property {string} school_admin_email - Email of the school admin
 */

/**
 * @typedef {object} SubSchoolListPaginated
 * @property {SubSchoolReportItem[]} data - Array of sub-school report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} SubSchoolReportData
 * @property {SubSchoolStats} stats - Overall statistics for all sub-schools
 * @property {SubSchoolListPaginated} schools - Paginated list of sub-schools with stats
 */

/**
 * @typedef {object} MetaType
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * Custom hook to fetch sub-school report with pagination
 */
export const useGetSubSchoolReport = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { axiosInstance } = useAxios();

    const [reportData, setReportData] = useState(
        /** @type {SubSchoolReportData | null | undefined} */(undefined)
    );
    const [metaData, setMetaData] = useState(
        /** @type {MetaType | null | undefined} */(undefined)
    );

    /**
     * Fetch sub-school report with pagination
     * @async
     * @param {number} [page] - Page number for pagination
     * @param {number} [per_page] - Number of items per page
     * @param {string} [search] - Search term to filter by name
     * @returns {Promise<void>}
     */
    const getSubSchoolReport = async (page = 1, per_page = ITEMS_PER_PAGE, search = "") => {
        setLoading(true);
        setError("");
        try {
            const query = new URLSearchParams();
            query.append("page", page.toString());
            query.append("per_page", per_page.toString());

            if (search) {
                query.append("filter[name]", search);
            }

            const url = `${TENANT_DOMAIN}/organization/admin/reports/sub-schools?${query.toString()}`;
            const response = await axiosInstance.get(url);
            const data = response.data?.data;

            setReportData(data);

            setMetaData({
                current_page: data?.schools?.current_page,
                per_page: data?.schools?.per_page,
                total: data?.schools?.total,
                last_page: data?.schools?.last_page,
            });
        } catch (err) {
            setReportData(null);
            setMetaData(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return { loading, error, reportData, getSubSchoolReport, metaData };
};